'use client';
import { useEffect } from 'react';
import { Article } from '@/types/data';

interface ArticleMetadataProps {
  article: Article | null;
}

const ArticleMetadata: React.FC<ArticleMetadataProps> = ({ article }) => {
  useEffect(() => {
    if (!article) return;

    // Update document title
    const stripHtml = (html: string) => {
      return html
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const title = stripHtml(article.title);
    document.title = `${title} | OME`;

    // Update meta description
    const description = stripHtml(article.content).substring(0, 160) + '...';
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Update Open Graph tags
    const updateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Update Twitter tags
    const updateTwitterTag = (name: string, content: string) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    // Update Open Graph tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', window.location.href);
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:image', article.image || '/images/default-article.jpg');
    updateMetaTag('og:site_name', 'OME');
    updateMetaTag('og:locale', 'vi_VN');

    // Update Twitter tags
    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', title);
    updateTwitterTag('twitter:description', description);
    updateTwitterTag('twitter:image', article.image || '/images/default-article.jpg');
    updateTwitterTag('twitter:creator', '@OME');
    updateTwitterTag('twitter:site', '@OME');

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', window.location.href);

    // Update structured data for article
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      image: article.image || '/images/default-article.jpg',
      author: {
        '@type': 'Person',
        name: 'OME Team',
      },
      publisher: {
        '@type': 'Organization',
        name: 'OME',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.khoahoconline-ome.edu.vn/Logo-OME-chot-ha-01.png',
        },
      },
      datePublished: new Date().toISOString(),
      dateModified: new Date().toISOString(),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': window.location.href,
      },
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, [article]);

  return null; // This component doesn't render anything
};

export default ArticleMetadata;
